/* FVS Step 12 and Step 21 */
/* eslint-disable react/jsx-one-expression-per-line */
import { ReactElement, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { iFVSFormValues } from '@services/FVSService';
import { FormType } from '@constants';
import { CheckboxInput, FileUploadGroup, Modal, Button } from '@components';
import { iFVSInfoDisplay } from '@interfaces/interfaces';
import styles from './SupportingDocuments.module.scss';

interface iSupportingDocuments {
  displayObject: iFVSInfoDisplay;
  currentStepNo: string;
}

function SupportingDocuments({ displayObject, currentStepNo } : iSupportingDocuments): ReactElement {
  const { values }: { values: iFVSFormValues } = useFormikContext();
  const [isCheckedUnable, setCheckedUnable] = useState(values.UnableToObtainAdoc || false);
  const [modal, setModal] = useState({
    show: false,
    title: '',
    content: <></>,
    contentClass: '',
  });
  const closeNotifications = () => {
    setModal({
      show: false,
      title: '',
      content: <></>,
      contentClass: '',
    });
  };
  const handleCheckBox = () => {
    setCheckedUnable((pre: boolean) => !pre);
  };
  const { pageTitle } = displayObject.pages.supportingDocuments[currentStepNo];
  const { isUnableToObtainADoc } = displayObject.pages.supportingDocuments[currentStepNo].fields;
  const notificationInformation = (): ReactElement => (
    <>
      <div className={styles.bitmapSection}>
        <p>A report by a relevant professional</p>
        <p>This can be a report, letter, or statement by:</p>
        <ul>
          <li>a lawyer</li>
          <li>a financial counsellor</li>
          <li>a case worker or social worker</li>
          <li>a family consultant or counsellor</li>
          <li>a psychologist</li>
          <li>a medical practitioner, a nurse, or other health practitioner</li>
          <li>a family violence practitioner from a family violence service (such as a women’s refuge, or an outreach, crisis or counselling service)</li>
          <li>an officer of a child welfare authority or child protection authority</li>
          <li>a school principal or school welfare coordinator</li>
          <li>a police officer, or a witness statement by someone other than you to a police officer, in the course of a police investigation</li>
          <li>
            any other person that the Director, Fines Victoria considers to be appropriate (please contact Fines Victoria FVS team to discuss via email:{' '}
            <a target="_blank" href="mailto: fvs@justice.vic.gov.au" rel="noreferrer">
              fvs@justice.vic.gov.au
            </a>{' '}
            or phone{' '}
            <a className={styles.contactLink} href="tel:1300 019 983" title="Fines Victoria team">
              1300 019 983
            </a>
            ). If a professional is writing a report to support your FVS application, you should ask them to include:
            <ul>
              <li>their full name, title, qualifications, and their employer</li>
            </ul>
          </li>
          <li>an outline and period of their service’s working relationship with you</li>
          <li>whether a family violence risk assessment and management plan (MARAM) has been completed with you</li>
          <li>
            their understanding and opinion of: – the period when you have experienced family violence – relevant ongoing impacts of the family violence, such as mental health or
            substance abuse issues – how the family violence substantially contributed to your inability to control the offending conduct, to nominate the actual driver, or to
            reject a nomination.
          </li>
        </ul>
        <span className={styles.bitmapTitle}>Generally, the report should be completed within the 12 months of you getting the fines or applying to the FVS.</span>
        <span className={styles.bitmapTitle}>
          If your application is based on mental health and substance abuse issues linked to family violence, you will generally be required to provide a report by a psychologist,
          psychiatrist, a general practitioner, or from a drug and alcohol counsellor.
        </span>
        <p className={styles.sectionTitle}>
          Please see{' '}
          <a target="_blank" href="https://online.fines.vic.gov.au/-/media/Files/Guide-to-the-Family-Violence-Scheme.pdf" rel="noreferrer">
            Guide to FVS
          </a>{' '}
          for more information about relevant professionals.
        </p>
        <div className={styles.btnFooter}>
          <Button onClick={closeNotifications} color="blue" size="small">
            Continue
          </Button>
        </div>
      </div>
    </>
  );
  return (
    <>
      <div className="vfH2">{pageTitle}</div>
      <p className="subHeadingH3">
        <Modal close={() => setModal({ ...modal, show: false })} {...modal} />
        Please attach a document prepared by a relevant authority or professional to support this application.
      </p>
      <p>A document prepared by a relevant authority or professional includes:</p>
      <ul>
        <li>a family violence intervention order</li>
        <li>a family violence safety notice</li>
        <li>
          a report, letter or statement from a{' '}
          <div className={styles.helpLinks}>
            <button
              type="button"
              onClick={() => {
                const content = notificationInformation();
                const title = 'Relevant professionals';
                setModal({
                  show: true,
                  title,
                  content,
                  contentClass: '',
                });
              }}>
              relevant professional
            </button>
          </div>{' '}
          (for example, a lawyer, financial counsellor, social worker or psychologist)
        </li>
      </ul>
      <p>Please attach document(s) (up to 4 documents, each document should be less than 4MB). A clear photo of the document from a mobile phone is okay.</p>
      <FileUploadGroup
        uploadAReport="ReportNameField"
        uploadFieldArrayName="SupportingAttachments"
        whoPrepared="SupportingFilesProvider"
        briefDescription="SupportingFilesDescription"
        fileSizeErrorField="ReportNameFieldErrorHandle"
        fileUploadFailErrorField="ReportNameFileUplodErrorHandle"
        UploadFileSectionError="ReportUploadFileSectionError"
        fileSizelimit="4"
        totalFileLimit={4}
        formType={FormType.FAMILYVIOLENCESCHEME}
      />
      <br />
      <Field
        className={styles.dropdownLayout}
        component={CheckboxInput}
        name={isUnableToObtainADoc.formikName}
        title={isUnableToObtainADoc.title}
        boxChecked={handleCheckBox}
        displayError
      />
      {isCheckedUnable && (
        <>
          <p className="vfH3">Please attach a signed statutory declaration</p>
          <p>If you do not have a document prepared by a relevant authority or professional, we require a signed statutory declaration.</p>
          <p>It should explain how you meet the eligibility criteria – especially the link between your experience of family violence and your fines.</p>
          <p>Your statutory declaration needs to be signed and witnessed by an authorised person.</p>
          <p>
            Visit{' '}
            <a target="_blank" href="https://justice.vic.gov.au/statdecs" rel="noreferrer">
              justice.vic.gov.au/statdecs
            </a>{' '}
            for a statutory declaration template and a list of people who can witness it for you.
          </p>
          <p>Please attach document (1 document, less than 4MB)</p>
          <Field
            component={FileUploadGroup}
            uploadAReport="SignedDeclarationNameField"
            uploadFieldArrayName="SignedDeclarationAttachment"
            whoPrepared="SignedStatutoryProvider"
            briefDescription="SignedStatutoryDescription"
            fileSizeErrorField="SignedStatutoryFieldErrorHandle"
            fileUploadFailErrorField="SignedStatutoryFileUploadErrorHandle"
            UploadFileSectionError="StatutoryUploadFileSectionError"
            fileSizelimit="4"
            totalFileLimit={1}
            hasPrepared={false}
            hasDescribe={false}
            formType={FormType.FAMILYVIOLENCESCHEME}
          />
        </>
      )}
    </>
  );
}

export default SupportingDocuments;
